// 此处用于存放所有登录相关的接口请求
import axios from 'axios'
import request from '@/utils/request'

// /////////////////////////////  登录、注册、密码  //////////////////////////////////

// 发送验证码
export const sendVerificationCodeApi = (userData) => {
  return request.post('/api/send_verification_code', userData)
}

// 注册
export const registerUser = (userData) => {
  return request.post('/api/register', userData)
}

// 手机验证码登录
export const smsLogin = (userData) => {
  return request.post('/api/sms_login', userData)
}

// 密码登录
export const passwordLogin = (userData) => {
  return request.post('/api/login', userData)
}

// 忘记密码
export const forgotPassword = (userData) => {
  return request.post('/api/forgot_password', userData)
}

// 保存用户头像图片
export const saveAvatarUrl = (formData) => {
  return request.post('/api/save_avatar_url', formData)
}

// 封装检查认证的函数
export const checkAuth = (token) => {
  return request.post('/api/check_auth', token)
}

// 获取用户信息，包括头像URL和账户余额
export const getUserInfo = () => {
  return request.get('/api/get_user_info')
}

// /////////////////////////////  七牛云令牌、文件上传  //////////////////////////////////

// 七牛云上传配置
const QINIU_BUCKET = 'prodata'
// 上传域名列表：按顺序尝试，网络层失败（无响应/超时）自动换下一个。
// 注意：上传域名必须与空间所在区域匹配，更换区域需先建同区域的空间。
//   up-z2 华南 / up-z0 华东 / up-z1 华北 / up-na0 北美 / up-as0 亚太
const QINIU_UPLOAD_HOSTS = [
  'https://up-z2.qiniup.com',
  'https://upload-z2.qiniup.com'
]
const CHUNKED_UPLOAD_THRESHOLD = 8 * 1024 * 1024 // 超过 8MB 使用分片上传
const DEFAULT_PART_SIZE = 4 * 1024 * 1024 // 分片大小 4MB
const DEFAULT_CONCURRENCY = 3 // 分片并发数
const PART_MAX_RETRY = 3 // 单个分片最多重试次数
const PART_TIMEOUT_MS = 120 * 1000 // 单个分片请求超时
const STALL_TIMEOUT_MS = 120 * 1000 // 长时间无进度视为卡死
const MAX_PART_COUNT = 9000 // 七牛限制约 10000 片，留出余量

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// 分片上传若因服务端返回错误（凭证/接口不可用）而失败，本次会话内不再尝试，
// 直接走一次性表单上传，避免每次都白跑一次失败流程。
let chunkedUploadDisabled = false

const createCancelError = () => {
  const err = new Error('上传已取消')
  err.canceled = true
  return err
}

const isCanceled = (err, signal) => !!(
  (signal && signal.aborted) ||
  (err && (err.canceled === true || err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError'))
)

const throwIfCanceled = signal => {
  if (signal && signal.aborted) throw createCancelError()
}

// 组合外部取消信号与内部卡死保护信号
const mergeSignal = (...signals) => {
  const valid = signals.filter(Boolean)
  if (valid.length === 0) return undefined
  if (valid.length === 1) return valid[0]
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  valid.forEach(s => {
    if (s.aborted) controller.abort()
    else s.addEventListener('abort', onAbort, { once: true })
  })
  return controller.signal
}

// 七牛云分片上传协议要求对象名做 URL 安全 Base64 编码
const urlSafeBase64 = input => {
  const bytes = new TextEncoder().encode(input)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_')
}

// 进度上报：支持分片累计与整包上报两种方式
const createProgressReporter = (totalSize, onProgress) => {
  const startedAt = Date.now()
  let loaded = 0
  const emit = () => {
    if (typeof onProgress !== 'function') return
    const elapsed = Math.max((Date.now() - startedAt) / 1000, 0.001)
    onProgress({
      loaded,
      total: totalSize,
      percent: totalSize ? Math.min(100, Number(((loaded * 100) / totalSize).toFixed(1))) : 0,
      speed: loaded / elapsed, // 字节/秒
      elapsed
    })
  }
  return {
    setLoaded (value) { loaded = value; emit() },
    add (delta) { loaded += delta; emit() },
    finish () { loaded = totalSize; emit() }
  }
}

// 获取上传文件的 token
// fileName 传入时后端会把凭证限定到该 key，并使用更长的有效期（大文件上传耗时更久）
export const getUpQiniuToken = (fileName = '', expires = 0) => {
  const payload = {}
  if (fileName) payload.file_name = fileName
  if (expires) payload.expires = expires
  return request.post('/api/get_upload_token', payload)
}

// 一次性表单上传（小文件、以及分片上传不可用时的回退方案）
const formUpload = (host, key, token, file, options, reporter) => new Promise((resolve, reject) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('key', key)
  formData.append('token', token)

  const stallController = new AbortController()
  let lastProgressAt = Date.now()
  let stalled = false
  const watchdog = setInterval(() => {
    if (Date.now() - lastProgressAt > options.stallTimeoutMs) {
      stalled = true
      stallController.abort()
    }
  }, 5000)

  axios.post(host, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal: mergeSignal(options.signal, stallController.signal),
    onUploadProgress: evt => {
      lastProgressAt = Date.now()
      reporter.setLoaded(evt.loaded || 0)
    }
  }).then(res => {
    clearInterval(watchdog)
    const data = res && res.data
    if (!data || !data.key) {
      reject(new Error('上传返回结果异常'))
      return
    }
    reporter.finish()
    resolve(data.key)
  }).catch(err => {
    clearInterval(watchdog)
    if (stalled) {
      const e = new Error('上传长时间无进度，请检查网络后重试')
      e.stalled = true
      reject(e)
      return
    }
    reject(err)
  })
})

// 分片上传（七牛分片上传 v2）：可分片并发、单片失败重试、断点续传，适合大文件
const chunkedUpload = async (host, key, token, file, options, reporter) => {
  const encodedKey = urlSafeBase64(key)
  const base = `${host}/buckets/${QINIU_BUCKET}/objects/${encodedKey}/uploads`
  const authHeaders = { Authorization: `UpToken ${token}` }

  const operationController = new AbortController()
  const signal = mergeSignal(options.signal, operationController.signal)

  try {
    // 1) 初始化分片上传
    const initRes = await axios.post(base, null, {
      headers: authHeaders,
      signal,
      timeout: 30000
    })
    const uploadId = initRes && initRes.data && initRes.data.uploadId
    if (!uploadId) throw new Error('分片上传初始化失败')

    // 2) 分片并发上传
    const basePartSize = options.partSize || DEFAULT_PART_SIZE
    const partSize = Math.max(
      basePartSize,
      Math.ceil(file.size / MAX_PART_COUNT / basePartSize) * basePartSize
    )
    const partCount = Math.ceil(file.size / partSize)
    const partEtags = new Array(partCount).fill('')
    const partLoaded = new Array(partCount).fill(0)
    let nextIndex = 0

    const uploadPart = async index => {
      const start = index * partSize
      const blob = file.slice(start, Math.min(start + partSize, file.size))
      const url = `${base}/${uploadId}/${index}`
      let lastError = null

      for (let attempt = 1; attempt <= PART_MAX_RETRY; attempt++) {
        throwIfCanceled(signal)
        partLoaded[index] = 0
        try {
          const res = await axios.put(url, blob, {
            headers: { ...authHeaders, 'Content-Type': 'application/octet-stream' },
            signal,
            timeout: PART_TIMEOUT_MS,
            onUploadProgress: evt => {
              partLoaded[index] = evt.loaded || 0
              reporter.setLoaded(partLoaded.reduce((sum, n) => sum + n, 0))
            }
          })
          const etag = (res.headers && (res.headers.etag || res.headers.Etag)) || ''
          if (!etag) throw new Error(`分片 ${index} 未返回 etag`)
          partLoaded[index] = blob.size
          reporter.setLoaded(partLoaded.reduce((sum, n) => sum + n, 0))
          return String(etag).replace(/"/g, '')
        } catch (err) {
          if (isCanceled(err, options.signal)) throw err
          lastError = err
          if (attempt < PART_MAX_RETRY) await sleep(1000 * attempt)
        }
      }
      throw lastError || new Error(`分片 ${index} 上传失败`)
    }

    const worker = async () => {
      while (true) {
        const index = nextIndex++
        if (index >= partCount) return
        partEtags[index] = await uploadPart(index)
      }
    }

    try {
      await Promise.all(
        new Array(Math.max(1, Math.min(options.concurrency, partCount))).fill(0).map(worker)
      )
    } catch (err) {
      operationController.abort()
      throw err
    }

    // 3) 完成分片上传
    const completeRes = await axios.post(`${base}/${uploadId}`, {
      parts: partEtags.map((etag, index) => ({ partNumber: index, etag })),
      fname: file.name,
      mimeType: file.type || 'application/octet-stream'
    }, {
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      signal: options.signal,
      timeout: PART_TIMEOUT_MS
    })

    reporter.finish()
    return (completeRes && completeRes.data && completeRes.data.key) || key
  } catch (err) {
    operationController.abort()
    throw err
  }
}

/**
 * 上传文件到七牛云（大文件自动分片并发上传，失败回退一次性表单上传）
 * @param {File} file 文件对象
 * @param {String} fileName 上传后的 key
 * @param {String} qiniuUploadToken 上传凭证
 * @param {Object} [options] { onProgress({loaded,total,percent,speed,elapsed}), signal, partSize, concurrency, stallTimeoutMs }
 * @returns {Promise<Object>} 形如 { data: { key, hash } }，与旧调用方式兼容
 */
export const uploadToQiniu = async (file, fileName, qiniuUploadToken, options = {}) => {
  if (!qiniuUploadToken) throw new Error('缺少上传凭证，请重新登录后重试')
  if (!file || !file.size) throw new Error('文件为空，无法上传')

  const opts = {
    partSize: DEFAULT_PART_SIZE,
    concurrency: DEFAULT_CONCURRENCY,
    stallTimeoutMs: STALL_TIMEOUT_MS,
    ...options
  }
  throwIfCanceled(opts.signal)

  const reporter = createProgressReporter(file.size, opts.onProgress)
  const key = fileName || file.name
  let lastError = null

  for (const host of QINIU_UPLOAD_HOSTS) {
    throwIfCanceled(opts.signal)

    if (file.size >= CHUNKED_UPLOAD_THRESHOLD) {
      if (!chunkedUploadDisabled) {
        try {
          const uploadedKey = await chunkedUpload(host, key, qiniuUploadToken, file, opts, reporter)
          return { data: { key: uploadedKey }, key: uploadedKey, mode: 'chunked' }
        } catch (err) {
          if (isCanceled(err, opts.signal)) throw createCancelError()
          lastError = err
          if (err && err.response) chunkedUploadDisabled = true
          console.warn('[qiniu] 分片上传失败，回退到普通上传：', err && err.message)
        }
      }
    }

    try {
      const uploadedKey = await formUpload(host, key, qiniuUploadToken, file, opts, reporter)
      return { data: { key: uploadedKey }, key: uploadedKey, mode: 'form' }
    } catch (err) {
      if (isCanceled(err, opts.signal)) throw createCancelError()
      lastError = err
      // 只在网络层失败（没有响应）时换下一个上传域名重试
      if (err && err.response) break
    }
  }

  throw lastError || new Error('上传失败')
}

// /////////////////////////////  音频  //////////////////////////////////

// 生成字幕
export const genSubtitle = (formData) => {
  return request.post('/api/audioSubtitle', formData)
}

// 人声分离
export const voiceSeparation = (formData) => {
  return request.post('/api/audioVoiceSeparation', formData)
}

// 【新增】音频原语音克隆翻译
export const audioVoiceCloneTranslation = (formData) => {
  return request.post('/api/audioVoiceCloneTranslation', formData)
}

// 视频一键翻译
export const saveoneclickVideoTranslateFileUrl = (formData) => {
  return request.post('/api/save_oneclick_video_translation_url', formData)
}

// 视频重新配音（上传视频 + 参考音频 + 视频文案）
export const saveVideoDubFileUrl = (formData) => {
  return request.post('/api/save_video_dub_url', formData)
}

// 【新增】分步视频翻译（精细视频翻译）
// 第一步：生成视频的 SRT 字幕
export const saveStepwiseVideoSrtFileUrl = (formData) => {
  return request.post('/api/stepwiseVideoSrt', formData)
}

// 上传修正字目前校验第一步任务是否已完成
export const validateStepwiseVideoSrtTask = (formData) => {
  return request.post('/api/stepwiseVideoSrt/validate', formData)
}

// 第二步：上传校准后的字幕，通过第一步 task_id 完成语音克隆翻译
export const saveStepwiseVideoTranslationFileUrl = (formData) => {
  return request.post('/api/stepwiseVideoTranslation', formData)
}

// 获取用户操作记录
export const getUserRecords = (requestData) => {
  // return 'wwwww'
  return request.post('/api/user-records', requestData)
}

// 获取用户操作记录
export const getRechargeRecords = (requestData) => {
  // return 'wwwww'
  return request.post('/api/user-recharge-records', requestData)
}

export const getAccountBalance = (requestData) => {
  // return 'wwwww'
  return request.post('/api/get-account-balance', requestData)
}

export const updateAccountBalance = (requestData) => {
  // return 'wwwww'
  return request.post('/api/update-account-balance', requestData)
}

// 获取服务器状态列表
export const getServersStatus = () => {
  return request.post('/api/get-servers-status')
}

// 删除服务器记录
export const deleteServer = (requestData) => {
  return request.post('/api/delete-server', requestData)
}

// 【新增】数据备份与恢复接口
export const saveTableData = (data) => {
  return request.post('/api/save-table-data', data)
}

export const restoreTableData = (data) => {
  return request.post('/api/restore-table-data', data)
}

export const getBackupList = () => {
  return request.get('/api/backup-list')
}

// 【新增】获取当前数据库中的数据表列表（自动包含新业务表）
export const getDbTables = () => {
  return request.get('/api/db-tables')
}

// 【新增】清除七牛云过期文件接口
export const cleanupExpiredRecords = () => {
  return request.post('/api/cleanup-expired-records')
}

// 【新增】管理员资金总览：全站累计充值 / 累计消费 / 账户余额 / 累计退款 + 各业务队列
export const getFundsOverview = () => {
  return request.get('/api/admin/funds-overview')
}

// 【新增】单个用户账户对账汇总：累计充值 / 累计消费 / 冻结中 / 账户余额 + 各业务使用明细
// * @param {Object} params - { phone }
export const getUserAccountSummary = (params) => {
  return request.get('/api/admin/user-account-summary', { params })
}

// 【新增】用户消费历史：跨业务表查询，支持业务类型 / 日期筛选与分页
// * @param {Object} params - { phone, business_type, start_date, end_date, page, page_size }
export const getUserConsumptionRecords = (params) => {
  return request.get('/api/admin/user-consumption-records', { params })
}

// 【新增】管理员退款接口
// * @param {Object} data - { user_phone, alipay_order_id, refund_amount, refund_reason }
// * @returns {Promise}
export const adminRefund = (data) => {
  return request.post('/api/admin/refund', data)
}

// * 【新增】查询订单退款信息（用于显示可退金额）
// * @param {Object} params - { phone, alipay_order_id }
// * @returns {Promise}
export const queryOrderInfo = (params) => {
  return request.get('/api/admin/order-refund-info', { params })
}

// * 【新增】查询退款记录列表（分页+筛选）
// * @param {Object} params - { phone, status, page, page_size }
// * @returns {Promise}
export const getRefundRecords = (params) => {
  return request.get('/api/admin/refund-records', { params })
}

export const queryUsersTable = (params) => {
  return request.get('/api/query-users', { params })
}

// 删除用户记录
export const deleteUserRecord = (recordId) => {
  // return 'wwwww'
  return request.post('/api/delete-records', recordId)
}

export const getPendingTasks = () => {
  // return 'wwwww'
  return request.post('/api/get-pending-tasks')
}

// 网页后端，查询用户操作记录
export const videosDownapi = (requestData) => {
  return request.post('/api/videosDown', requestData)
}
